import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    IDLE, PENDING, YES, VALUE, UNLIMITED, ACTIVE, VIEW,
} from "const"
import {
    mtSettingsRoute,
    projectSettingsRoute,
    projectsRoute,
} from "client/parts/routes"
import {
    replaceHistoryState,
    hasAccess,
    hasProjectAccess,
    StateType,
} from "client/utils"
import {
    PRM_ACCOUNT_SETTINGS,
    PRM_PROJECT_SETTINGS,
    StoreType,
    ProjectActiveModules,

    LoadAccountSettings,
    LoadProjectSettings,
    LoadUserSettings,
    SaveAccountSettings,
    SaveProjectSettings,
    SaveUserSettings,

    setAccountSettings,
    setProjectSettings,
    setProjectProjectSettings,
    setUserSettings,
    setUserSettingsReloadList,
    setSystemAccountSettings,
    setSystemUserSettings,
    moduleIds,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuDropdown,
    PageMenuDropdownItem,
    Tab,
    Loader,
    PageForm,
    Button,
    SpinnerIcon,
} from "client/components"

import ImportDefaultSettings from "./importSettings"
import ImportDefaultProjectSettings from "./importProjectSettings"

import Mail from "./mail"
import Project from "./project"
import Drive from "./drive"
import Study from "./study"
import Okr from "./okr"
import Cc from "./cc"
import Sil from "./sil"


const MAIL = "#mail"
const PROJECT = "#project"
const DRIVE = "#drive"
const STUDY = "#study"
const OKR = "#okr"
const CC = "#cc"
const SIL = "#sil"

let menu: string[] = []
const menuTitle: { [K: string]: string } = {}

let isMounted: boolean

interface SettingsProps {
    hash?: string
}

const Settings = ({
    hash = null,
}: SettingsProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.accountSettings.settings)
    const projectSettings = useSelector((state: StoreType) => state.projectSettings.settings)
    const userSettings = useSelector((state: StoreType) => state.userSettings.settings)
    const reloadList = useSelector((state: StoreType) => state.userSettings.reloadList)

    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState<string>(null)
    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    const viewAccess = !project.id
        ? hasAccess(PRM_ACCOUNT_SETTINGS, VIEW)
        : hasProjectAccess(PRM_PROJECT_SETTINGS, VIEW)

    const projectActiveModules: ProjectActiveModules = project.modules ? JSON.parse(project.modules) : {}

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        // create dropdown menu
        const {
            projectModuleAccess,
            driveSpace,
            driveSpaceValue,
            studyModuleAccess,
            okrModuleAccess,
            ccModuleAccess,
            silModuleAccess,
        } = packageSettings
        const {
            study,
            cc,
            sil,
        } = moduleIds
        const isSuperAdmin = userData.accountId === 1

        let driveAccess = false
        if (driveSpace === VALUE && driveSpaceValue > 0) driveAccess = true
        if (driveSpace === UNLIMITED) driveAccess = true

        menu = []

        // super admin only settings
        if (isSuperAdmin) {
            menu.push(MAIL)
            menuTitle[MAIL] = lang.mail
        }

        // project settings, both on super admin and account level
        if (
            !project.id
            && (isSuperAdmin || projectModuleAccess === YES)
        ) {
            menu.push(PROJECT)
            menuTitle[PROJECT] = lang.project
        }

        // drive settings, both on super admin and account level
        if (
            !project.id
            && (isSuperAdmin || driveAccess)
        ) {
            menu.push(DRIVE)
            menuTitle[DRIVE] = lang.drive
        }

        // study settings, all levels
        if (
            (isSuperAdmin || (studyModuleAccess === YES && moduleList[study].status === ACTIVE && viewAccess))
            && (project.id ? projectActiveModules[study] : true) // on project level - show only if module enabled
        ) {
            menu.push(STUDY)
            menuTitle[STUDY] = moduleList[study].name
        }

        // okr settings, both on super admin and account level
        if (
            !project.id
            && (isSuperAdmin || (okrModuleAccess === YES && viewAccess))
        ) {
            menu.push(OKR)
            menuTitle[OKR] = lang.objectivesKeyResults
        }

        // sil settings, both on super admin and account level
        if (
            !project.id
            && (
                isSuperAdmin
                || (silModuleAccess === YES && moduleList[sil].status === ACTIVE && viewAccess)
            )
        ) {
            menu.push(SIL)
            menuTitle[SIL] = moduleList[sil].name
        }

        // compliance case settings, all levels
        if (
            (isSuperAdmin || (ccModuleAccess === YES && moduleList[cc].status === ACTIVE && viewAccess))
            && (project.id ? projectActiveModules[cc] : true) // on project level - show only if module enabled
        ) {
            menu.push(CC)
            menuTitle[CC] = moduleList[cc].name
        }

        const defaultTab = menu.length ? menu[0] : null
        setActiveTab(menu.includes(hash) ? hash : defaultTab)
    }, [project])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        if (!project.id) {
            ImportDefaultSettings(userData.accountId, dispatch).then(() => {
                Promise.all([
                    LoadUserSettings(userData.userId, userData.accountId).then((res) => {
                        dispatch(setUserSettings(res))
                    }),
                    LoadAccountSettings(userData.accountId).then((res) => {
                        dispatch(setAccountSettings(res))
                    }),
                ]).then(() => {
                    setLoadState(IDLE)
                })
            })
        } else {
            ImportDefaultProjectSettings(project, dispatch).then(() => (
                LoadProjectSettings(project.id).then((res) => {
                    dispatch(setProjectSettings(res))
                    setLoadState(IDLE)
                })
            ))
        }
    }, [reloadList, project])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        if (!project.id) {
            replaceHistoryState(`${mtSettingsRoute}${tab}`)
        } else {
            replaceHistoryState(`${projectsRoute}/${VIEW}/${project.id}${projectSettingsRoute}${tab}`)
        }
    }

    const onCancel = () => {
        dispatch(setUserSettingsReloadList(false))
        setTimeout(() => {
            dispatch(setUserSettingsReloadList(true))
        }, 0)
    }

    const onApply = () => {
        setApplyState(PENDING)
        const promiseArray: Promise<any>[] = [
            SaveUserSettings(userData.userId, userData.accountId, userSettings)
            .then(() => {
                dispatch(setSystemUserSettings(userSettings))
            }),
        ]

        if (!project.id) {
            promiseArray.push(
                SaveAccountSettings(userData.accountId, accountSettings)
                .then(() => {
                    dispatch(setSystemAccountSettings(accountSettings))
                }),
            )
        } else {
            promiseArray.push(
                SaveProjectSettings(project.id, projectSettings)
                .then(() => {
                    dispatch(setProjectProjectSettings(projectSettings))
                }),
            )
        }

        Promise.all(promiseArray).then(() => {
            if (isMounted) setApplyState(IDLE)
        })
    }

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.moduleSettings}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <PageMenu>
                    <PageMenuDropdown title={activeTab ? menuTitle[activeTab] : ""}>
                        {
                            menu.map((item) => (
                                <PageMenuDropdownItem
                                    key={item}
                                    onClick={() => handleMenuClick(item)}
                                >
                                    {menuTitle[item]}
                                </PageMenuDropdownItem>
                            ))
                        }
                    </PageMenuDropdown>
                </PageMenu>

                <Tab show={activeTab === MAIL}>
                    <Mail />
                </Tab>
                <Tab show={activeTab === PROJECT}>
                    <Project />
                </Tab>
                <Tab show={activeTab === DRIVE}>
                    <Drive />
                </Tab>
                <Tab show={activeTab === STUDY}>
                    <Study />
                </Tab>
                <Tab show={activeTab === OKR}>
                    <Okr />
                </Tab>
                <Tab show={activeTab === CC}>
                    <Cc />
                </Tab>
                <Tab show={activeTab === SIL}>
                    <Sil />
                </Tab>

                {
                    // save user/account level setings
                    (
                        activeTab === PROJECT
                        || activeTab === STUDY
                        || activeTab === OKR
                        || activeTab === SIL
                        || activeTab === DRIVE
                    ) && (
                        <PageForm>
                            <Row>
                                <Col xs={12}>
                                    <Button variant="success" row onClick={() => onApply()}>
                                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                                    </Button>
                                    <Button row onClick={onCancel}>{lang.cancel}</Button>
                                </Col>
                            </Row>
                        </PageForm>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default Settings
